import { connectQuickInterview } from "./api";
import type { QuickInterviewSttMode } from "./api";

const DATA_CHANNEL_OPEN_TIMEOUT_MS = 15_000;

interface RealtimeSessionOptions {
  onEvent: (event: string) => void;
  onConnectionStateChange: (state: RTCPeerConnectionState) => void;
  onInputLevel?: (level: number) => void;
  onOutputLevel?: (level: number) => void;
}

export class QuickRealtimeSession {
  private peerConnection: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private localStream: MediaStream | null = null;
  private audioElement: HTMLAudioElement | null = null;
  private audioContext: AudioContext | null = null;
  private inputAnalyser: AnalyserNode | null = null;
  private inputLevelFrame: number | null = null;
  private outputAnalyser: AnalyserNode | null = null;
  private outputLevelFrame: number | null = null;
  private muted = false;

  constructor(private readonly options: RealtimeSessionOptions) {}

  async prepareMedia() {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("このブラウザはマイク入力に対応していません。");
    }

    this.audioElement = document.createElement("audio");
    this.audioElement.autoplay = true;
    this.audioElement.setAttribute("playsinline", "true");
    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    this.startInputLevelMonitoring();
  }

  async connect(sessionId: string, sttMode: QuickInterviewSttMode = "realtime") {
    if (!this.localStream || !this.audioElement) {
      throw new Error("マイクが初期化されていません。");
    }

    const peerConnection = new RTCPeerConnection();
    this.peerConnection = peerConnection;
    peerConnection.onconnectionstatechange = () => {
      this.options.onConnectionStateChange(peerConnection.connectionState);
    };
    peerConnection.ontrack = (event) => {
      const remoteStream = event.streams[0] ?? new MediaStream([event.track]);
      if (!this.audioElement) return;
      this.audioElement.srcObject = remoteStream;
      this.audioElement.play().catch(() => {});
      this.startOutputLevelMonitoring(remoteStream);
    };

    if (sttMode === "realtime") {
      this.localStream.getTracks().forEach((track) => {
        peerConnection.addTrack(track, this.localStream as MediaStream);
      });
    } else {
      peerConnection.addTransceiver("audio", { direction: "recvonly" });
    }

    const dataChannel = peerConnection.createDataChannel("oai-events");
    this.dataChannel = dataChannel;
    dataChannel.onmessage = (event) => {
      if (typeof event.data === "string") this.options.onEvent(event.data);
    };

    const offer = await peerConnection.createOffer();
    await peerConnection.setLocalDescription(offer);
    const offerSdp = peerConnection.localDescription?.sdp;
    if (!offerSdp) throw new Error("音声接続情報を作成できませんでした。");

    const answerSdp = await connectQuickInterview(sessionId, offerSdp);
    await peerConnection.setRemoteDescription({
      type: "answer",
      sdp: answerSdp,
    });
    await this.waitForDataChannel(dataChannel);
  }

  sendEvent(event: object) {
    if (this.dataChannel?.readyState !== "open") return false;
    this.dataChannel.send(JSON.stringify(event));
    return true;
  }

  getLocalStream() {
    return this.localStream;
  }

  createTextInput(itemId: string, text: string) {
    return this.sendEvent({
      type: "conversation.item.create",
      item: {
        id: itemId,
        type: "message",
        role: "user",
        content: [{ type: "input_text", text }],
      },
    });
  }

  setMuted(muted: boolean) {
    this.muted = muted;
    this.localStream?.getAudioTracks().forEach((track) => {
      track.enabled = !muted;
    });
    if (muted) this.options.onInputLevel?.(0);
  }

  stopInput() {
    this.localStream?.getAudioTracks().forEach((track) => {
      track.enabled = false;
    });
  }

  cancelResponse() {
    this.sendEvent({ type: "response.cancel" });
  }

  // instructions を渡すと、その応答に限りセッションの面接指示を上書きする。
  // 締めの応答で、モデルの回答回数カウントに依存せず締め文言を話させるために使う
  createResponse(instructions?: string) {
    if (!instructions) return this.sendEvent({ type: "response.create" });

    return this.sendEvent({
      type: "response.create",
      response: { instructions },
    });
  }

  deleteItem(itemId: string) {
    return this.sendEvent({
      type: "conversation.item.delete",
      item_id: itemId,
    });
  }

  close() {
    this.stopOutputLevelMonitoring();
    this.stopInputLevelMonitoring();
    this.localStream?.getTracks().forEach((track) => track.stop());
    this.localStream = null;

    if (this.dataChannel) {
      this.dataChannel.onmessage = null;
      this.dataChannel.close();
      this.dataChannel = null;
    }

    if (this.peerConnection) {
      this.peerConnection.ontrack = null;
      this.peerConnection.onconnectionstatechange = null;
      this.peerConnection.close();
      this.peerConnection = null;
    }

    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.srcObject = null;
      this.audioElement.remove();
      this.audioElement = null;
    }
  }

  private createAnalyser(stream: MediaStream) {
    const AudioContextClass = window.AudioContext;
    if (!AudioContextClass) return null;
    this.audioContext ??= new AudioContextClass();
    const source = this.audioContext.createMediaStreamSource(stream);
    const analyser = this.audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.75;
    source.connect(analyser);
    return analyser;
  }

  private measureLevel(analyser: AnalyserNode, samples: Uint8Array<ArrayBuffer>) {
    analyser.getByteTimeDomainData(samples);
    let sumSquares = 0;
    for (const sample of samples) {
      const centered = (sample - 128) / 128;
      sumSquares += centered * centered;
    }
    return Math.min(1, Math.sqrt(sumSquares / samples.length) * 5);
  }

  private startInputLevelMonitoring() {
    if (!this.localStream || !this.options.onInputLevel) return;

    const analyser = this.createAnalyser(this.localStream);
    if (!analyser) return;
    this.inputAnalyser = analyser;
    const samples = new Uint8Array(analyser.fftSize);

    const measure = () => {
      if (!this.inputAnalyser) return;
      const level = this.measureLevel(this.inputAnalyser, samples);
      this.options.onInputLevel?.(this.muted ? 0 : level);
      this.inputLevelFrame = window.requestAnimationFrame(measure);
    };
    measure();
  }

  // 面接官の音声が実際に鳴っているかを判定するために出力側も監視する。
  // 締めの発話が鳴り終わったことを、文字数からの推測ではなく音量で検知する
  private startOutputLevelMonitoring(stream: MediaStream) {
    if (!this.options.onOutputLevel || this.outputAnalyser) return;

    const analyser = this.createAnalyser(stream);
    if (!analyser) return;
    this.outputAnalyser = analyser;
    const samples = new Uint8Array(analyser.fftSize);

    const measure = () => {
      if (!this.outputAnalyser) return;
      this.options.onOutputLevel?.(
        this.measureLevel(this.outputAnalyser, samples),
      );
      this.outputLevelFrame = window.requestAnimationFrame(measure);
    };
    measure();
  }

  private stopInputLevelMonitoring() {
    if (this.inputLevelFrame !== null) {
      window.cancelAnimationFrame(this.inputLevelFrame);
      this.inputLevelFrame = null;
    }
    this.inputAnalyser?.disconnect();
    this.inputAnalyser = null;
    this.closeAudioContext();
    this.options.onInputLevel?.(0);
  }

  private stopOutputLevelMonitoring() {
    if (this.outputLevelFrame !== null) {
      window.cancelAnimationFrame(this.outputLevelFrame);
      this.outputLevelFrame = null;
    }
    this.outputAnalyser?.disconnect();
    this.outputAnalyser = null;
    this.closeAudioContext();
    this.options.onOutputLevel?.(0);
  }

  // 入力・出力で解析器を共有しているため、両方止まってから破棄する。
  // 双方の停止処理から呼び、呼び出し順に依存しないようにする
  private closeAudioContext() {
    if (this.inputAnalyser || this.outputAnalyser) return;
    if (!this.audioContext) return;
    void this.audioContext.close();
    this.audioContext = null;
  }

  private waitForDataChannel(dataChannel: RTCDataChannel) {
    if (dataChannel.readyState === "open") return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        cleanup();
        reject(new Error("音声接続がタイムアウトしました。"));
      }, DATA_CHANNEL_OPEN_TIMEOUT_MS);
      const cleanup = () => {
        window.clearTimeout(timeout);
        dataChannel.removeEventListener("open", handleOpen);
        dataChannel.removeEventListener("error", handleError);
      };
      const handleOpen = () => {
        cleanup();
        resolve();
      };
      const handleError = () => {
        cleanup();
        reject(new Error("音声接続を開始できませんでした。"));
      };
      dataChannel.addEventListener("open", handleOpen);
      dataChannel.addEventListener("error", handleError);
    });
  }
}
